import {
  trace,
  context,
  SpanStatusCode,
  type Exception,
  type Span,
} from "@opentelemetry/api";

export const tracer = trace.getTracer("service");
export { trace, context };

/**
 * Tracing HOF that takes the original function and returns a new function with tracing logic
 */
// TODO: The arg types aren't quite working here.
export function withTracing<
  TFunc extends (...args: TArgs) => Promise<TOut> | TOut,
  TArgs extends unknown[],
  TOut,
>(spanName: string, fn: TFunc): TFunc {
  // Return a wrapped function with the same signature as the original
  return ((...args: TArgs) =>
    tracer.startActiveSpan(spanName, (span) => {
      try {
        // Call the original function with all its arguments
        const result = fn(...args);
        if (result instanceof Promise) {
          return result
            .then((out: TOut) => out)
            .catch((error) => {
              setSpanErrorDetails(error, span);
              throw error;
            })
            .finally(() => {
              span.end();
            });
        } else {
          span.end();
          return result;
        }
      } catch (error: unknown) {
        setSpanErrorDetails(error, span);
        throw error;
      }
    })) as TFunc;
}

export function setSpanErrorDetails(err: unknown, span?: Span) {
  span = span ?? trace.getActiveSpan();
  if (!span) return;

  if (isException(err)) {
    span.recordException(err);
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: typeof err === "string" ? err : err.message,
    });
  } else {
    span.recordException(new Error("unknown error", { cause: err }));
    span.setStatus({ code: SpanStatusCode.ERROR, message: "unknown error" });
  }
}

function isException(err: unknown): err is Exception {
  return (
    typeof err === "string" ||
    err instanceof Error ||
    (typeof err === "object" &&
      !Array.isArray(err) &&
      err !== null &&
      ("code" in err || "message" in err || "name" in err))
  );
}

export function addSpanEvent(...args: Parameters<Span["addEvent"]>) {
  const span = trace.getActiveSpan();
  if (span) span.addEvent(...args);
}

export function setSpanAttr(...args: Parameters<Span["setAttribute"]>) {
  const span = trace.getActiveSpan();
  if (span) span.setAttribute(...args);
}
